import { DeviceParts, type AssetInfo as RendererAsset } from "@cva/renderer";
import {
  createDeviceElement,
  DEVICE_CATEGORIES,
  DEVICE_CATEGORY_LABELS,
  DEVICE_MODELS,
  DEVICE_TILT_LIMIT,
  type DeviceCategory,
  type DeviceElement,
  type DeviceTilt,
  deviceBoxSize,
  deviceCanRotate,
  deviceContentAspect,
  deviceFinish,
  deviceForScreenshot,
  deviceModel,
  devicePerspectives,
  matchingPerspective,
  searchDevices,
} from "@cva/shared";
import { ImagePlus, Search, Smartphone, Sparkles, Upload, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { MediaPickerDialog } from "../components/media-picker";
import { Input, Select } from "../components/ui/input";
import { cn } from "../lib/utils";
import { type AssetInfo, replaceImage } from "./commands";
import { toAssetInfo, useEnv } from "./env";
import { addDevice, type ScreenshotInfo, tiltFor, updateDevices } from "./mockup-commands";
import { PanelTitle } from "./PanelTitle";
import { useEditor, useEditorApi } from "./store";
import { Row, Section, Segmented, Slider, Toggle } from "./ui";
import { addDeviceClip, setOverlayDevice, videoIntoDevice } from "./video/commands";
import { useVideoApi, useVideoMaybe } from "./video/store";

/**
 * Mockup mode (docs/19-mockups.md): the "Mockups" panel puts a screenshot
 * into a phone, tablet, laptop, screen or browser window with one click; the
 * properties panel changes device, colour, orientation and perspective.
 */

/** Views offered for inserting (each device maps them to its own presets). */
const INSERT_VIEWS: { id: string; label: string }[] = [
  { id: "front", label: "Gerade" },
  { id: "hero", label: "Schräg" },
  { id: "right", label: "Rechts" },
  { id: "left", label: "Links" },
  { id: "lying", label: "Liegend" },
];

/** A device drawn into a square of `size` pixels (panel tiles, perspective buttons). */
export function DeviceThumb({
  device,
  landscape = false,
  tilt,
  finish,
  asset,
  size,
  glare = false,
}: {
  device: string;
  landscape?: boolean;
  tilt: DeviceTilt;
  finish?: string | undefined;
  asset?: RendererAsset | undefined;
  size: number;
  glare?: boolean;
}) {
  const element = useMemo(() => {
    const props = { device, landscape, tilt, ...(finish ? { finish } : {}) };
    const box = deviceBoxSize(props, deviceContentAspect(asset), { width: size, height: size });
    return createDeviceElement({ ...props, ...box, glare, ...(asset ? { assetId: "thumb" } : {}) });
  }, [device, landscape, tilt, finish, asset, size, glare]);
  return (
    <div
      className="pointer-events-none relative"
      style={{ width: element.width, height: element.height }}
    >
      <DeviceParts element={element} asset={asset} idPrefix="thumb" />
    </div>
  );
}

/**
 * The picture for the screen: an image, or (video documents) a video – its
 * poster frame stands in for it in the previews.
 */
function useScreenshot(
  assetId: string | null | undefined,
  fallback?: AssetInfo | null,
): {
  info: ScreenshotInfo | null;
  asset: RendererAsset | undefined;
  video: AssetInfo | null;
} {
  const env = useEnv();
  if (!assetId) return { info: null, asset: undefined, video: null };
  const known = env.assets.get(assetId) ?? (fallback?.id === assetId ? fallback : undefined);
  if (known?.kind === "video") {
    return {
      info: { id: known.id, width: known.width, height: known.height, name: known.name },
      asset: known.thumbnailUrl
        ? { url: known.thumbnailUrl, width: known.width, height: known.height }
        : undefined,
      video: known,
    };
  }
  const asset = env.resolveAsset(assetId);
  return {
    info: known
      ? { id: known.id, width: known.width, height: known.height, name: known.name }
      : { id: assetId, width: asset?.width ?? null, height: asset?.height ?? null },
    asset,
    video: null,
  };
}

// ---------------------------------------------------------------------------
// Left panel
// ---------------------------------------------------------------------------

/** `bare`: without the title bar (inside Elemente, which shows its own). */
export function MockupsPanel({ bare = false }: { bare?: boolean }) {
  const store = useEditorApi();
  const env = useEnv();
  const videoApi = useVideoApi();
  const readOnly = useEditor((s) => s.readOnly);
  const selectedImage = useEditor((s) => {
    const element = s.selection.length === 1 ? s.doc.elements[s.selection[0]!] : undefined;
    return element?.type === "image" ? element.assetId : null;
  });
  const selectedDevice = useEditor((s) => {
    const element = s.selection.length === 1 ? s.doc.elements[s.selection[0]!] : undefined;
    return element?.type === "device" && !element.locked ? element : null;
  });
  // Video documents: a selected clip on an upper track goes into the device clicked.
  const selectedClipId = useVideoMaybe(
    (state) => (state.selectedClip?.track === "overlay" ? state.selectedClip.id : null),
    null,
  );
  const selectedClip = useEditor((s) =>
    selectedClipId
      ? (s.doc.timeline?.overlays?.find((clip) => clip.id === selectedClipId) ?? null)
      : null,
  );
  const [picked, setPicked] = useState<string | null>(null);
  const [pickedAsset, setPickedAsset] = useState<AssetInfo | null>(null);
  const [picking, setPicking] = useState(false);
  const [category, setCategory] = useState<DeviceCategory | null>(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("front");
  const fileInput = useRef<HTMLInputElement>(null);
  const screenshotId =
    picked ?? selectedImage ?? selectedDevice?.assetId ?? selectedClip?.assetId ?? null;
  const { info, asset, video } = useScreenshot(screenshotId, pickedAsset);
  // Video documents also take screen recordings (they become clips in a device).
  const accepts = videoApi ? ["image/", "video/"] : ["image/"];
  const suggestion = info?.width ? deviceForScreenshot(info) : null;
  const models = useMemo(() => searchDevices(query, category), [query, category]);

  if (readOnly) {
    return (
      <p className="p-4 text-sm text-zinc-500">
        Im Ansichtsmodus lassen sich keine Mockups einfügen.
      </p>
    );
  }

  const sizeOf = (assetId: string): ScreenshotInfo | undefined => {
    const known = env.assets.get(assetId);
    return known ? { id: known.id, width: known.width, height: known.height } : undefined;
  };
  // With a mockup selected, the panel changes it; otherwise it inserts a new one.
  const insert = (device: string) => {
    if (selectedClip) {
      const source = env.assets.get(selectedClip.assetId);
      const model = deviceModel(device);
      const landscape =
        deviceCanRotate(model) && source?.width && source.height
          ? source.width > source.height !== model.screen.width > model.screen.height
          : false;
      setOverlayDevice(
        store,
        selectedClip.id,
        {
          ...(selectedClip.device ?? { glare: false }),
          device,
          landscape,
          tilt: tiltFor(device, view),
          ...(selectedClip.device?.device === device && selectedClip.device.finish
            ? { finish: selectedClip.device.finish }
            : { finish: undefined }),
        },
        source,
      );
    } else if (video && videoApi) {
      addDeviceClip(store, videoApi, video, { device, view });
    } else if (selectedDevice) {
      const landscape =
        deviceCanRotate(deviceModel(device)) && info?.width && info.height
          ? info.width > info.height !==
            deviceModel(device).screen.width > deviceModel(device).screen.height
          : false;
      updateDevices(
        store,
        [selectedDevice.id],
        { device, landscape, tilt: tiltFor(device, view) },
        sizeOf,
      );
    } else {
      addDevice(store, { device, screenshot: info, view });
    }
  };
  const chooseView = (next: string) => {
    setView(next);
    if (selectedClip?.device) {
      setOverlayDevice(
        store,
        selectedClip.id,
        { ...selectedClip.device, tilt: tiltFor(selectedClip.device.device, next) },
        env.assets.get(selectedClip.assetId),
      );
    } else if (selectedDevice) {
      updateDevices(
        store,
        [selectedDevice.id],
        { tilt: tiltFor(selectedDevice.device, next) },
        sizeOf,
      );
    }
  };

  return (
    <>
      {bare ? null : <PanelTitle>Mockups</PanelTitle>}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="space-y-3 border-b border-zinc-100 p-4">
          {info && asset ? (
            <div className="flex items-center gap-3">
              <img
                src={asset.url}
                alt=""
                className="size-14 shrink-0 rounded-md border border-zinc-200 bg-zinc-50 object-contain"
              />
              <div className="min-w-0 flex-1 text-xs">
                <p className="truncate font-medium text-zinc-800">{info.name ?? "Screenshot"}</p>
                <p className="text-zinc-500">
                  {info.width && info.height ? `${info.width} × ${info.height} px` : null}
                  {!picked && selectedImage ? " · ausgewähltes Bild" : null}
                  {!picked && !selectedImage && selectedDevice ? " · im Mockup" : null}
                  {video ? " · Video" : null}
                </p>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    className="font-medium text-brand-700 hover:underline"
                    onClick={() => setPicking(true)}
                  >
                    Ändern
                  </button>
                  {picked ? (
                    <button
                      type="button"
                      className="text-zinc-500 hover:underline"
                      onClick={() => setPicked(null)}
                    >
                      Entfernen
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            // biome-ignore lint/a11y/noStaticElementInteractions: drop target, the buttons inside upload or pick
            <div
              className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3 text-center"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const assetId = event.dataTransfer.getData("application/x-cva-asset");
                const kind = assetId ? env.assets.get(assetId)?.kind : undefined;
                if (assetId && (kind === "image" || (videoApi && kind === "video"))) {
                  setPicked(assetId);
                  return;
                }
                const files = [...event.dataTransfer.files].filter((file) =>
                  accepts.some((type) => file.type.startsWith(type)),
                );
                if (files.length > 0) env.uploadAndInsert(files.slice(0, 1), { mockup: { view } });
              }}
            >
              <Smartphone className="mx-auto mb-1.5 size-6 text-zinc-400" />
              <p className="text-xs text-zinc-600">
                {videoApi
                  ? "Screenshot oder Bildschirmaufnahme wählen – das passende Gerät wird vorgeschlagen."
                  : "Screenshot wählen – das passende Gerät wird vorgeschlagen."}
              </p>
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 text-xs font-medium text-white hover:bg-brand-700 [&_svg]:size-3.5"
                >
                  <Upload /> Hochladen
                </button>
                <button
                  type="button"
                  onClick={() => setPicking(true)}
                  className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white text-xs font-medium text-zinc-700 hover:bg-zinc-50 [&_svg]:size-3.5"
                >
                  <ImagePlus /> Mediathek
                </button>
              </div>
              <input
                ref={fileInput}
                type="file"
                accept={videoApi ? "image/*,video/*" : "image/*"}
                className="hidden"
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])];
                  event.target.value = "";
                  if (files.length > 0)
                    env.uploadAndInsert(files.slice(0, 1), { mockup: { view } });
                }}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <p className="text-[11px] font-medium text-zinc-500">Ansicht</p>
            <div className="flex flex-wrap gap-1">
              {INSERT_VIEWS.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => chooseView(entry.id)}
                  className={cn(
                    "h-7 rounded-full border px-2.5 text-xs",
                    view === entry.id
                      ? "border-brand-500 bg-brand-50 text-brand-700"
                      : "border-zinc-200 text-zinc-600 hover:bg-zinc-50",
                  )}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {selectedDevice || selectedClip ? (
          <p className="border-b border-zinc-100 bg-brand-50/60 px-4 py-2.5 text-xs text-brand-800">
            {selectedClip
              ? "Ein Klick auf ein Gerät oder eine Ansicht zeigt den ausgewählten Clip darin."
              : "Ein Klick auf ein Gerät oder eine Ansicht ändert das ausgewählte Mockup."}
          </p>
        ) : null}
        {suggestion ? (
          <div className="border-b border-zinc-100 p-4">
            <p className="mb-2 flex items-center gap-1 text-[11px] font-medium text-brand-700 [&_svg]:size-3.5">
              <Sparkles /> Passt zu deinem Screenshot
            </p>
            <DeviceTile
              device={suggestion.device}
              landscape={suggestion.landscape}
              view={view}
              asset={asset}
              wide
              onClick={() => insert(suggestion.device)}
            />
          </div>
        ) : null}
        <div className="space-y-2.5 p-4">
          <div className="relative">
            <Search className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-zinc-400" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Gerät suchen, z. B. iPhone, Pixel, MacBook"
              className="h-9 pl-8 text-sm"
              aria-label="Gerät suchen"
            />
          </div>
          <Select
            className="h-9 text-sm"
            value={category ?? ""}
            onChange={(event) => setCategory((event.target.value || null) as DeviceCategory | null)}
            aria-label="Gerätetyp"
          >
            <option value="">Alle Geräte</option>
            {DEVICE_CATEGORIES.map((entry) => (
              <option key={entry} value={entry}>
                {DEVICE_CATEGORY_LABELS[entry]}
              </option>
            ))}
          </Select>
          {models.length === 0 ? (
            <p className="py-4 text-center text-xs text-zinc-500">Kein Gerät gefunden.</p>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            {models.map((model) => (
              <DeviceTile
                key={model.id}
                device={model.id}
                landscape={
                  suggestion && info?.width && info.height && deviceCanRotate(model)
                    ? info.width > info.height !== model.screen.width > model.screen.height
                    : false
                }
                view={view}
                asset={asset}
                onClick={() => insert(model.id)}
              />
            ))}
          </div>
          <p className="pt-1 text-[11px] text-zinc-500">
            Tipp: Ein Bild aus der Mediathek direkt auf ein Gerät im Design ziehen, um den
            Screenshot zu tauschen.
          </p>
        </div>
      </div>
      {picking ? (
        <MediaPickerDialog
          orgId={env.orgId}
          open
          onOpenChange={setPicking}
          title={videoApi ? "Screenshot oder Video wählen" : "Screenshot wählen"}
          description={
            videoApi
              ? "Bild oder Bildschirmaufnahme – ein Video läuft als Clip im Gerät."
              : "Das Bild kommt auf den Bildschirm des Geräts."
          }
          kinds={videoApi ? ["video", "image"] : ["image"]}
          onPick={([chosen]) => {
            setPicking(false);
            if (chosen?.kind === "image" || (videoApi && chosen?.kind === "video")) {
              setPicked(chosen.id);
              setPickedAsset(toAssetInfo(chosen));
            }
          }}
        />
      ) : null}
    </>
  );
}

function DeviceTile({
  device,
  landscape,
  view,
  asset,
  wide = false,
  onClick,
}: {
  device: string;
  landscape: boolean;
  view: string;
  asset: RendererAsset | undefined;
  wide?: boolean;
  onClick: () => void;
}) {
  const model = deviceModel(device);
  const tilt = tiltFor(device, view);
  return (
    <button
      type="button"
      onClick={onClick}
      title={model.name}
      className={cn(
        "group flex flex-col items-center gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 p-2 transition hover:border-brand-300 hover:bg-brand-50",
        wide && "w-full flex-row gap-3 bg-brand-50/40",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center",
          wide ? "size-20" : "h-24 w-full",
        )}
      >
        <DeviceThumb
          device={device}
          landscape={landscape}
          tilt={tilt}
          asset={asset}
          size={wide ? 76 : 88}
        />
      </div>
      <span
        className={cn(
          "text-[11px] leading-tight text-zinc-700 group-hover:text-brand-700",
          wide ? "text-left text-sm font-medium" : "text-center",
        )}
      >
        {model.name}
        {wide ? (
          <span className="block text-xs font-normal text-zinc-500">
            {model.screen.width} × {model.screen.height} px
          </span>
        ) : null}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

const TILT_FIELDS: { key: keyof DeviceTilt; label: string; title: string }[] = [
  { key: "y", label: "Drehen", title: "Nach links oder rechts drehen" },
  { key: "x", label: "Neigen", title: "Nach hinten oder vorne neigen" },
  { key: "z", label: "Kippen", title: "In der Bildschirmebene drehen" },
];

export function DeviceProperties({ element }: { element: DeviceElement }) {
  const store = useEditorApi();
  const env = useEnv();
  const videoApi = useVideoApi();
  const [picking, setPicking] = useState(false);
  const model = deviceModel(element.device);
  const finish = deviceFinish(model, element.finish);
  const { info, asset } = useScreenshot(element.assetId);
  const locked = element.locked;
  const sizeOf = (assetId: string): ScreenshotInfo | undefined => {
    const known = env.assets.get(assetId);
    return known ? { id: known.id, width: known.width, height: known.height } : undefined;
  };
  const change = (next: Parameters<typeof updateDevices>[2], merge?: string) =>
    updateDevices(store, [element.id], next, sizeOf, merge);
  const perspectives = devicePerspectives(element.device);
  const current = matchingPerspective(element.device, element.tilt);
  const grouped = DEVICE_CATEGORIES.map((category) => ({
    category,
    models: DEVICE_MODELS.filter((entry) => entry.category === category),
  }));

  return (
    <>
      <Section title="Mockup">
        <Select
          className="h-9 text-sm"
          value={element.device}
          disabled={locked}
          onChange={(event) => change({ device: event.target.value })}
          aria-label="Gerät"
        >
          {grouped.map((group) => (
            <optgroup key={group.category} label={DEVICE_CATEGORY_LABELS[group.category]}>
              {group.models.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
        {model.finishes.length > 1 ? (
          <div className="space-y-1.5">
            <p className="text-xs text-zinc-500">
              Farbe: <span className="text-zinc-800">{finish.name}</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {model.finishes.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  title={entry.name}
                  aria-label={entry.name}
                  aria-pressed={entry.id === finish.id}
                  disabled={locked}
                  onClick={() => change({ finish: entry.id })}
                  className={cn(
                    "size-7 rounded-full border border-black/10 ring-offset-2 transition",
                    entry.id === finish.id
                      ? "ring-2 ring-brand-500"
                      : "hover:ring-2 hover:ring-zinc-300",
                  )}
                  style={{
                    background: `linear-gradient(135deg, ${entry.frame} 0%, ${entry.frame} 55%, ${entry.side} 100%)`,
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}
        {deviceCanRotate(model) ? (
          <Segmented
            className="w-full"
            value={element.landscape ? "landscape" : "portrait"}
            onChange={(value) => change({ landscape: value === "landscape" })}
            options={[
              { value: "portrait", label: "Hochformat", title: "Gerät hochkant" },
              { value: "landscape", label: "Querformat", title: "Gerät quer" },
            ]}
          />
        ) : null}
        {model.spec.kind === "browser" ? (
          <label className="block space-y-1">
            <span className="text-xs text-zinc-500">Adresse in der Adressleiste</span>
            <Input
              value={element.url ?? ""}
              maxLength={200}
              disabled={locked}
              placeholder="deine-seite.de"
              onChange={(event) => change({ url: event.target.value || undefined }, "device-url")}
              className="h-9 text-sm"
            />
          </label>
        ) : null}
      </Section>
      <Section title="Perspektive">
        <div className="grid grid-cols-3 gap-1.5">
          {perspectives.map((entry) => (
            <button
              key={entry.id}
              type="button"
              disabled={locked}
              onClick={() => change({ tilt: { ...entry.tilt } })}
              className={cn(
                "flex flex-col items-center gap-1 rounded-lg border p-1.5 text-[11px] transition",
                current === entry.id
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-zinc-200 text-zinc-600 hover:bg-zinc-50",
              )}
            >
              <div className="flex h-12 items-center justify-center">
                <DeviceThumb
                  device={element.device}
                  landscape={element.landscape}
                  tilt={entry.tilt}
                  finish={element.finish}
                  asset={asset}
                  size={44}
                />
              </div>
              {entry.label}
            </button>
          ))}
        </div>
        {TILT_FIELDS.map((field) => (
          <label key={field.key} className="block space-y-1" title={field.title}>
            <span className="flex justify-between text-xs text-zinc-500">
              {field.label}
              <span className="tabular-nums text-zinc-700">
                {Math.round(element.tilt[field.key])}°
              </span>
            </span>
            <Slider
              value={element.tilt[field.key]}
              min={-DEVICE_TILT_LIMIT[field.key]}
              max={DEVICE_TILT_LIMIT[field.key]}
              onChange={(value) =>
                !locked && change({ tilt: { ...element.tilt, [field.key]: value } }, "device-tilt")
              }
            />
          </label>
        ))}
      </Section>
      <Section title="Bildschirm">
        {info && asset ? (
          <Row>
            <img
              src={asset.url}
              alt=""
              className="size-10 shrink-0 rounded border border-zinc-200 bg-zinc-50 object-contain"
            />
            <span className="min-w-0 flex-1 truncate text-xs text-zinc-700">
              {info.name ?? "Screenshot"}
            </span>
            <button
              type="button"
              disabled={locked}
              title="Screenshot entfernen"
              aria-label="Screenshot entfernen"
              onClick={() =>
                store.getState().update((draft) => {
                  const target = draft.elements[element.id];
                  if (target?.type === "device") delete target.assetId;
                })
              }
              className="inline-flex size-7 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100"
            >
              <X className="size-4" />
            </button>
          </Row>
        ) : (
          <p className="text-xs text-zinc-500">
            {videoApi
              ? "Zieh einen Screenshot oder ein Video (Bildschirmaufnahme) auf das Gerät – oder wähle es hier aus."
              : "Zieh einen Screenshot aus der Mediathek oder vom Computer auf das Gerät – oder wähle ihn hier aus."}
          </p>
        )}
        <button
          type="button"
          disabled={locked}
          onClick={() => setPicking(true)}
          className={cn(
            "inline-flex h-8 w-full items-center justify-center gap-1.5 rounded-md text-sm font-medium disabled:opacity-50",
            info
              ? "border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
              : "bg-brand-600 text-white hover:bg-brand-700",
          )}
        >
          <ImagePlus className="size-4" />{" "}
          {videoApi
            ? "Screenshot oder Video wählen"
            : info
              ? "Screenshot ersetzen"
              : "Screenshot wählen"}
        </button>
        {info ? (
          <Segmented
            className="w-full"
            value={element.fit}
            onChange={(fit) =>
              store.getState().update((draft) => {
                const target = draft.elements[element.id];
                if (target?.type === "device") target.fit = fit;
              })
            }
            options={[
              { value: "cover", label: "Ausfüllen", title: "Der Screenshot füllt den Bildschirm" },
              { value: "contain", label: "Ganz zeigen", title: "Den ganzen Screenshot zeigen" },
            ]}
          />
        ) : null}
        <Toggle
          checked={element.glare}
          onChange={(glare) =>
            store.getState().update((draft) => {
              const target = draft.elements[element.id];
              if (target?.type === "device") target.glare = glare;
            })
          }
          label="Spiegelung auf dem Glas"
        />
      </Section>
      {picking ? (
        <MediaPickerDialog
          orgId={env.orgId}
          open
          onOpenChange={setPicking}
          title={videoApi ? "Screenshot oder Video wählen" : "Screenshot wählen"}
          description={
            videoApi
              ? "Ein Bild kommt auf den Bildschirm; ein Video macht aus dem Mockup einen Clip im Gerät."
              : "Das Bild kommt auf den Bildschirm; das Gerät dreht sich passend dazu."
          }
          kinds={videoApi ? ["image", "video"] : ["image"]}
          onPick={([pickedAsset]) => {
            setPicking(false);
            if (videoApi && pickedAsset?.kind === "video") {
              videoIntoDevice(store, videoApi, element.id, toAssetInfo(pickedAsset));
              return;
            }
            if (pickedAsset?.kind !== "image") return;
            replaceImage(
              store,
              element.id,
              pickedAsset.id,
              pickedAsset,
              element.assetId ? sizeOf(element.assetId) : null,
            );
          }}
        />
      ) : null}
    </>
  );
}
